import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import { Autocomplete, Box, Chip, Stack, TextField, FormHelperText } from '@mui/material';
// components
import TextMaxLine from '../../../components/TextMaxLine';
import { FormProvider, RHFSelect, RHFSwitch, RHFTextField } from '../../../components/hook-form';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { apiErrorText } from 'src/utils/apiErrorText';
import { Track } from 'src/@types/tracks';
import { Category } from 'src/@types/Category';
import { UploadAvatar, UploadMultiFile } from 'src/components/upload';
import { addKeyword, firstErrorMessage, removeKeywordAt } from '../category/categoryPayload';
import { orderText, trackPayload, trackSchema } from './trackSchema';

// ----------------------------------------------------------------------

type KeywordKey = 'keywords' | 'keywords_ar';

type Props = {
  isEdit: boolean;
  currentTrack?: Track;
  handleClose(): void;
};

export default function TrackNewEditForm({ isEdit, currentTrack, handleClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [categories, setCategories] = useState<Category[]>([]);
  useEffect(() => {
    getCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onUploadCertificate = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(APIS.UPLOAD.DOCUMENT, form);
    return response.data;
  };
  const getCategories = async () => {
    try {
      const response = await axiosInstance.get(APIS.CATEGORIES.LIST);
      const list = response.data?.data ?? response.data;
      setCategories(Array.isArray(list) ? list : []);
    } catch (err) {
      enqueueSnackbar(apiErrorText(err, 'Could not load the paths'), { variant: 'error' });
    }
  };
  // Rules = the API's (trackSchema.ts, unit-tested).
  const NewTrackSchema = trackSchema;
  const defaultValues: any = useMemo(
    () => ({
      title: currentTrack?.title || '',
      title_ar: currentTrack?.title_ar || '',
      image: currentTrack?.image || '',
      briefVideo: currentTrack?.briefVideo || '',
      icon: currentTrack?.icon || '',
      certificateUrl: currentTrack?.certificateUrl || '',
      description: currentTrack?.description || '',
      description_ar: currentTrack?.description_ar || '',
      active: currentTrack?.active || false,
      isDiplomaLive: currentTrack?.isDiplomaLive || false,
      isCareerTrack: currentTrack?.isCareerTrack || false,
      // Text, so a stored 0 shows as "0" (RHFTextField blanks a numeric 0).
      order: orderText(currentTrack?.order),
      category: currentTrack?.category?._id || '',
      keywords: currentTrack?.keywords || [],
      keywords_ar: currentTrack?.keywords_ar || [],
    }),
    [currentTrack]
  );

  const methods = useForm<any>({
    resolver: yupResolver(NewTrackSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    setValue,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = methods;

  const values = watch();

  useEffect(() => {
    if (isEdit && currentTrack) {
      reset({
        ...currentTrack,
        category: currentTrack?.category?._id,
        order: orderText(currentTrack?.order),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, currentTrack]);

  const onSubmit = async (data: any) => {
    const body = trackPayload(data);
    try {
      if (currentTrack)
        await axiosInstance.patch(APIS.TRACKS.PATCH(currentTrack._id as string), body);
      else await axiosInstance.post(APIS.TRACKS.ADD, body);
      reset();
      enqueueSnackbar(!isEdit ? 'Create success!' : 'Update success!');
      handleClose();
    } catch (err) {
      // The axios instance rejects with the response body or a plain string
      // (network failure): one snackbar with every message, the modal stays
      // open with the values kept (T3 — `err.messages.length` used to throw).
      enqueueSnackbar(apiErrorText(err, !isEdit ? 'Unable to create Track' : 'Unable to edit Track'), {
        variant: 'error',
      });
    }
  };

  const uploadImage = async (file: File, field: 'icon' | 'image') => {
    const form = new FormData();
    form.append('file', file);
    try {
      const response = await axiosInstance.post(APIS.UPLOAD.MEDIA, form);
      const url = (response?.data as any)?.url ?? (response?.data as any)?.data?.url;
      if (url) setValue(field, url, { shouldValidate: true });
    } catch (err) {
      enqueueSnackbar(apiErrorText(err, 'Upload failed'), { variant: 'error' });
    }
  };
  const keywordError = (key: KeywordKey): string => firstErrorMessage(errors[key]);

  const keywordInput = (key: KeywordKey, label: string) => (
    <Autocomplete
      multiple
      id={key}
      options={[]}
      value={values?.[key] ?? []}
      freeSolo
      renderTags={(value: string[]) =>
        value.map((option: string, index: number) => (
          <Chip
            key={`${option}-${index}`}
            variant="outlined"
            label={option}
            onDelete={() =>
              setValue(key, removeKeywordAt(values?.[key], index), { shouldValidate: !!errors[key] })
            }
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          error={!!keywordError(key)}
          helperText={keywordError(key) || 'Press Enter after each keyword (3 characters or more)'}
          name={key}
          onKeyDown={(e) => {
            const input = e.target as HTMLInputElement;
            if (e.key === 'Enter') {
              e.preventDefault();
              setValue(key, addKeyword(values?.[key], input?.value), { shouldValidate: !!errors[key] });
            }
            // Backspace removes the last keyword only from an empty input.
            if (e.key === 'Backspace' && !input?.value && (values?.[key] || []).length) {
              setValue(key, (values?.[key] || []).slice(0, -1));
            }
          }}
          variant="outlined"
          label={label}
          placeholder="Keywords"
          maxRows={3}
        />
      )}
    />
  );

  return (
    <Stack height={'100%'} style={{ overflow: 'auto' }}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <TextMaxLine marginBottom={'0px'} marginTop={'0px'} fontSize={'22px'}>
          {isEdit ? ' Edit Track' : ' Create Track'}
        </TextMaxLine>
        <Stack direction={'column'} justifyContent={'space-between'} height={'90%'}>
          <Stack spacing={3}>
            <Stack direction={'row'} justifyContent={'center'} spacing={9}>
              <Stack direction={'column'}>
                <UploadAvatar
                  label="Track Icon"
                  accept={{ 'image/*': [] }}
                  error={errors.icon ? true : false}
                  file={values.icon}
                  onDropAccepted={(file) => uploadImage(file[0], 'icon')}
                />
                <p
                  style={{
                    color: '#FF4842',
                    width: '100%',
                    textAlign: 'center',
                    fontSize: '0.75rem',
                    marginTop: '10px',
                  }}
                >
                  {errors?.icon && String(errors?.icon?.message)}
                </p>
              </Stack>
              <Stack>
                <UploadAvatar
                  label="Track Image"
                  accept={{ 'image/*': [] }}
                  error={errors.image ? true : false}
                  file={values.image}
                  onDropAccepted={(file) => uploadImage(file[0], 'image')}
                />
                <p
                  style={{
                    color: '#FF4842',
                    width: '100%',
                    textAlign: 'center',
                    fontSize: '0.75rem',
                    marginTop: '10px',
                  }}
                >
                  {errors?.image && String(errors?.image?.message)}
                </p>
              </Stack>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                columnGap: 1,
                rowGap: 1,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="title" label="English Track Title" />
              <RHFTextField name="title_ar" label="Arabic Track Title" />
            </Box>
            {/* Phase: user segmentation — subscribers of this track = "Diploma Live" */}
            <RHFSwitch
              name="isDiplomaLive"
              label="Diploma Live track (subscribers count as the Diploma Live segment)"
            />
            {/* The student site's Tracks page leads with career tracks (SOC, Web, Data…);
                everything else (tips, free sessions, crash courses) is listed under "Start small". */}
            <RHFSwitch
              name="isCareerTrack"
              label="Career track (shown first on the website's Tracks page; off = tips, free sessions or crash courses)"
            />
            <RHFTextField
              name="order"
              type="number"
              label="Display order (lower numbers are shown first on the website)"
              helperText="0 to 1000. Tracks with the same number keep their creation order."
              inputProps={{ step: 'any', inputMode: 'numeric', 'data-testid': 'track-order' }}
            />
            <RHFSelect value={values.category} name="category" label="Path" placeholder="Path">
              <option value="" />
              {categories?.map((option: Category) => (
                <option key={option._id} value={option._id}>
                  {option.title}
                </option>
              ))}
            </RHFSelect>
            <RHFTextField name="briefVideo" label="Track Brief Video Url" />

            <RHFTextField
              multiline
              minRows={2}
              name="description"
              maxRows={3}
              label="English Description"
            />
            <RHFTextField
              multiline
              minRows={2}
              name="description_ar"
              maxRows={3}
              label="Arabic Description"
            />
            {keywordInput('keywords', 'Keywords')}
            {keywordInput('keywords_ar', 'Arabic Keywords')}
            <UploadMultiFile
              error={
                errors.certificateUrl && (values.certificateUrl === '' || !values.certificateUrl)
                  ? true
                  : false
              }
              disableControl={true}
              accept={{ 'application/pdf': ['.pdf'] }}
              files={values.certificateUrl ? [values.certificateUrl] : []}
              maxFiles={1}
              label={'Select Or Drop Certificate Url'}
              onDropAccepted={async (file) => {
                const response = await onUploadCertificate(file[0]);
                setValue('certificateUrl', response.url);
              }}
            />
            {errors.certificateUrl && (values.certificateUrl === '' || !values.certificateUrl) && (
              <FormHelperText error sx={{ px: 2, textAlign: 'start' }}>
                {errors?.certificateUrl?.message ?? ('certificateUrl is required' as any)}
              </FormHelperText>
            )}
          </Stack>
          <Stack alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton
              style={{ width: '200px', height: '50px' }}
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              {!isEdit ? 'Create Track' : 'Save Changes'}
            </LoadingButton>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
}
